import { Percent } from "@silo/types";
import {
  CollateralCurrentState,
  CollateralMetrics,
  CollateralNewState,
} from "./types";
import { Option, pipe } from "effect";
import { isNullable } from "effect/Predicate";
import {
  getAprState,
  getProjectionsState,
  getUtilizationState,
} from "../utils";
import { ProjectionsState } from "../types";
import { getCollateralAprMetrics } from "@silo/utils";

interface GetCollateralCurrentMetricsParams {
  currentState: CollateralCurrentState;
  nativeRewardsSymbol: string;
  uopt: Percent;
  ucrit: Percent;
}

export function getCollateralCurrentMetrics({
  currentState,
  nativeRewardsSymbol,
  uopt,
  ucrit,
}: GetCollateralCurrentMetricsParams): CollateralMetrics {
  const { collateralBaseApr, projections, utilization } = currentState;

  const {
    hasCollateralRewards,
    collateralRewards,
    totalCollateralApr,
    totalCollateralRewardsApr,
  } = getCollateralAprMetrics(currentState, nativeRewardsSymbol);

  const projectionsState = pipe(
    projections,
    Option.fromNullable,
    Option.map(({ collateralBaseApr1d, collateralBaseApr3d }) =>
      getProjectionsState({
        baseApr: collateralBaseApr,
        baseApr1d: collateralBaseApr1d,
        baseApr3d: collateralBaseApr3d,
      })
    ),
    Option.getOrElse((): ProjectionsState => ({ _tag: "none" }))
  );

  const utilizationState = getUtilizationState({
    utilization,
    uopt,
    ucrit,
  });

  const aprState = getAprState(projectionsState, utilizationState);

  const currentMetrics: CollateralMetrics = {
    utilization,
    collateralBaseApr,
    hasCollateralRewards,
    collateralRewards,
    totalCollateralApr,
    totalCollateralRewardsApr,
    projectionsState,
    utilizationState,
    aprState,
  };

  return currentMetrics;
}

interface GetCollateralNewMetricsParams {
  newState: CollateralNewState | null;
  nativeRewardsSymbol: string;
  uopt: Percent;
  ucrit: Percent;
}

export function getCollateralNewMetrics({
  newState,
  nativeRewardsSymbol,
  uopt,
  ucrit,
}: GetCollateralNewMetricsParams): CollateralMetrics | null {
  if (isNullable(newState)) return null;

  const {
    projections,
    collateralRewardsApr,
    collateralMerklRewards,
    utilization,
  } = newState;

  if (isNullable(projections)) return null;

  const { collateralBaseApr, collateralBaseApr1d, collateralBaseApr3d } =
    projections;

  const {
    hasCollateralRewards,
    collateralRewards,
    totalCollateralApr,
    totalCollateralRewardsApr,
  } = getCollateralAprMetrics(
    { collateralBaseApr, collateralRewardsApr, collateralMerklRewards },
    nativeRewardsSymbol
  );

  const projectionsState = getProjectionsState({
    baseApr: collateralBaseApr,
    baseApr1d: collateralBaseApr1d,
    baseApr3d: collateralBaseApr3d,
  });

  const utilizationState = getUtilizationState({
    utilization,
    uopt,
    ucrit,
  });

  const aprState = getAprState(projectionsState, utilizationState);

  const newMetrics: CollateralMetrics = {
    utilization,
    collateralBaseApr,
    hasCollateralRewards,
    collateralRewards,
    totalCollateralApr,
    totalCollateralRewardsApr,
    projectionsState,
    utilizationState,
    aprState,
  };

  return newMetrics;
}
